import {
  HbrOption,
  HbrSelect,
  HbrTextarea,
} from '@harbor/elements-react-standalone';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useFetchUser } from '../../../hooks/user-hooks';
import { ProductSupportPath } from '../../../hooks/useProductSupportPaths';
import { postItem, putItem } from '../../../support/queries';
import {
  ApprovalRequestStatusLevel,
  EscalationLevel,
  EscalationRequest,
} from '../../../types/approval-request';
import StbApiModal from '../../security-toolbox-ui/StbApiModal';
import { CacheKey } from '../../../types/cache-keys';
import { queryClient } from '../../../support/queryClient';

type FormValues = {
  specialist: string | null;
  managerNote: string;
};

const initialValues: FormValues = {
  specialist: null,
  managerNote: '',
};

const IncubationAssignmentModal: React.FC<{
  escalation: EscalationRequest;
  supportPath: ProductSupportPath;
  currentAssignment?: any;
  open: boolean;
  closeModal: () => Promise<void>;
}> = ({ escalation, supportPath, currentAssignment, open, closeModal }) => {
  const { data: user } = useFetchUser();

  const {
    control,
    handleSubmit,
    reset,
    formState,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const selectedSpecialist = watch('specialist');
  const [specialistOptions, setSpecialistOptions] = useState<any[]>([]);

  const isManager = !!user?.is_manager;

  const teamMembers = useMemo(() => {
    const members = supportPath?.group_members ?? [];

    if (members.length > 0) {
      return members;
    }

    if (user) {
      return [
        {
          id: user.id,
          email: user.email,
        },
      ];
    }

    return [];
  }, [supportPath, user]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (isManager) {
      setSpecialistOptions(
        teamMembers.map((member) => ({
          id: member.id,
          email:
            Number(member.id) === Number(user.id)
              ? `${member.email} (assign myself)`
              : member.email,
        }))
      );

      return;
    }

    setSpecialistOptions([
      {
        id: user.id,
        email: `${user.email} (assign myself)`,
      },
    ]);

    setValue('specialist', String(user.id), {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [user, isManager, teamMembers, setValue]);

  const onClose = async () => {
    reset(initialValues);
    await closeModal();
  };

  const onSubmit = async (data: FormValues) => {
    if (!user || !escalation || !supportPath) {
      return;
    }

    const note = isManager ? data.managerNote?.trim() || null : null;

    const assignmentPayload = {
      id: currentAssignment?.id,
      escalation_id: (escalation as any).id,
      assignment_type_id: EscalationLevel.Incubation,
      status_id: ApprovalRequestStatusLevel.Completed,
      assigned_to_id: Number(data.specialist),
      product_support_id: supportPath.id,
      notes: note,
      requested_by_id: user.id,
    };

    if (currentAssignment?.id) {
      await putItem('db/escalation-assignment', assignmentPayload);
    } else {
      const createPayload = { ...assignmentPayload };
      delete (createPayload as any).id;
      await postItem('db/escalation-assignment', createPayload);
    }

    await queryClient.invalidateQueries({ queryKey: [CacheKey.Escalations], refetchType: 'all' });

    if (escalation?.id && escalation?.opportunity?.id) {
      queryClient.invalidateQueries({ queryKey: [
        CacheKey.EscalationRequest,
        escalation?.opportunity?.id,
        escalation?.id
      ].map(String), refetchType: 'all' })
    }

    reset(initialValues);
    await closeModal();
  };

  return (
    <StbApiModal
      open={open}
      title={`Assign Specialist: ${supportPath?.product_name || (escalation as any)?.product_name || ''}`}
      role="save"
      submitLabel="Assign Specialist"
      submitDisabled={
        !selectedSpecialist || Object.keys(formState.errors).length > 0
      }
      submitSuccess="Request assigned successfully"
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
    >
      <form>
        <Controller
          name="specialist"
          control={control}
          rules={{
            validate: (value) =>
              value ? true : 'Incubation team specialist is required',
          }}
          render={({ field }) => (
            <HbrSelect
              value={field.value}
              disabled={!isManager}
              onHbr-change={(e) => field.onChange(e.target.value)}
              placeholder="Select a specialist..."
            >
              <div slot="label">
                Incubation SE{' '}
                <em className="text-gray-500"> (required)</em>
              </div>

              {(specialistOptions ?? []).map((option) => (
                <HbrOption key={option.id} value={String(option.id)}>
                  {option.email}
                </HbrOption>
              ))}
            </HbrSelect>
          )}
        />

        {isManager && (
          <Controller
            name="managerNote"
            control={control}
            render={({ field }) => (
              <HbrTextarea
                value={field.value}
                onHbr-input={(e) => field.onChange(e.target.value)}
                rows={3}
              >
                <div slot="label">
                  Assignment Note{' '}
                  <em className="text-gray-500">(optional)</em>
                </div>
              </HbrTextarea>
            )}
          />
        )}
      </form>
    </StbApiModal>
  );
};

export default IncubationAssignmentModal;